import React from 'react';
import { PayoutCalculationStrategy } from 'src/modules/conversions/domain';
import { PayoutTermDTO } from 'src/modules/conversions/infra/dtos';
import { useCurrency } from 'src/modules/currencies';

import { Skeleton } from '@/design-system/components/ui';
import { maskAddressValue } from '@/utils/format';

import { FormattedAmount } from '../FormattedAmount';

interface Props {
  payoutTerm: PayoutTermDTO;
}

export const PayoutAmounts = ({ payoutTerm }: Props) => {
  const {
    calculation_strategy: calculationStrategy,
    payout_currency_address: payoutCurrencyAddress,
    payout_currency_chain_id: payoutCurrencyChainId,
    base_currency: payoutBaseCurrency,
    referrer_amount: referrerAmount,
    referral_amount: referralAmount,
    referrer2_amount: referrer2Amount,
    referrer3_amount: referrer3Amount,
    referrer4_amount: referrer4Amount,
    type,
    trigger_amount_source: amountSource,
  } = payoutTerm;

  const isFixedPayment = calculationStrategy === PayoutCalculationStrategy.Fixed;
  const isPointsReward = type === 'point';

  const { data: currency, isLoading: isCurrencyLoading } = useCurrency(
    isPointsReward ? undefined : payoutCurrencyAddress,
    isPointsReward ? undefined : payoutCurrencyChainId?.toString(),
  );

  const payoutCurrencySymbol = isPointsReward ? 'POINTS' : (currency?.name ?? maskAddressValue(payoutCurrencyAddress));
  const payoutCurrencyDecimals = isPointsReward ? 0 : (currency?.decimals ?? 18);
  const payoutCurrencyLogoUri = isPointsReward ? null : (currency?.logoUri ?? null);

  const hasPayoutBaseCurrency = !!payoutBaseCurrency && payoutBaseCurrency.toLocaleLowerCase() != 'none';
  const payoutBaseCurrencySymbol = hasPayoutBaseCurrency ? payoutBaseCurrency?.toUpperCase() : payoutCurrencySymbol;

  const showSkeleton = isCurrencyLoading && !isPointsReward;
  const isMultilevel = Boolean(referrer2Amount);

  return (
    <div className="tw:flex tw:flex-col tw:gap-[0.8rem] tw:py-[0.2rem] tw:w-fit tw:border-l-[0.2rem] tw:border-l-[var(--mui-palette-primary-main)] tw:pl-2">
      {showSkeleton ? (
        <Skeleton width={120} height={20} />
      ) : (
        <FormattedAmount
          {...{
            amount: referrerAmount,
            userType: 'affiliate',
            payoutCurrencyAddress,
            payoutCurrencyDecimals,
            payoutCurrencySymbol,
            payoutCurrencyLogoUri,
            payoutBaseCurrencySymbol,
            payoutCurrencyChainId,
            isFixedPayment,
            isPointsReward,
            hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
            width: '200px',
            amountSource: amountSource ?? undefined,
            level: isMultilevel ? 1 : undefined,
          }}
        />
      )}
      {referrer2Amount &&
        (showSkeleton ? (
          <Skeleton width={120} height={20} />
        ) : (
          <FormattedAmount
            {...{
              amount: referrer2Amount,
              userType: 'affiliate',
              payoutCurrencyAddress,
              payoutCurrencyDecimals,
              payoutCurrencySymbol,
              payoutCurrencyLogoUri,
              payoutBaseCurrencySymbol,
              payoutCurrencyChainId,
              isFixedPayment,
              isPointsReward,
              hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
              width: '200px',
              amountSource: amountSource ?? undefined,
              level: 2,
            }}
          />
        ))}
      {referrer3Amount &&
        (showSkeleton ? (
          <Skeleton width={120} height={20} />
        ) : (
          <FormattedAmount
            {...{
              amount: referrer3Amount,
              userType: 'affiliate',
              payoutCurrencyAddress,
              payoutCurrencyDecimals,
              payoutCurrencySymbol,
              payoutCurrencyLogoUri,
              payoutBaseCurrencySymbol,
              payoutCurrencyChainId,
              isFixedPayment,
              isPointsReward,
              hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
              width: '200px',
              amountSource: amountSource ?? undefined,
              level: 3,
            }}
          />
        ))}
      {referrer4Amount &&
        (showSkeleton ? (
          <Skeleton width={120} height={20} />
        ) : (
          <FormattedAmount
            {...{
              amount: referrer4Amount,
              userType: 'affiliate',
              payoutCurrencyAddress,
              payoutCurrencyDecimals,
              payoutCurrencySymbol,
              payoutCurrencyLogoUri,
              payoutBaseCurrencySymbol,
              payoutCurrencyChainId,
              isFixedPayment,
              isPointsReward,
              hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
              width: '200px',
              amountSource: amountSource ?? undefined,
              level: 4,
            }}
          />
        ))}
      {showSkeleton ? (
        <Skeleton width={120} height={20} />
      ) : (
        <FormattedAmount
          {...{
            amount: referralAmount,
            userType: 'endUser',
            payoutCurrencyAddress,
            payoutCurrencyDecimals,
            payoutCurrencySymbol,
            payoutCurrencyLogoUri,
            payoutBaseCurrencySymbol,
            payoutCurrencyChainId,
            isFixedPayment,
            isPointsReward,
            hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
            width: '200px',
            amountSource: amountSource ?? undefined,
          }}
        />
      )}
    </div>
  );
};
